import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Server, Plus, Database, Network, CheckCircle2, XCircle, Loader2, Shield, Globe,
  Settings, Trash2, Edit3, Eye, AlertCircle, Cpu, HardDrive, Wifi, Lock, EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SAPLandscape = ({ project }) => {
  const [landscapes, setLandscapes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateLandscape, setShowCreateLandscape] = useState(false);
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [selectedLandscape, setSelectedLandscape] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [expandedSystems, setExpandedSystems] = useState({});

  useEffect(() => {
    fetchLandscapes();
  }, [project.id]);

  const fetchLandscapes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/landscapes`);
      setLandscapes(response.data);
    } catch (error) {
      console.error('Error fetching landscapes:', error);
    } finally {
      setLoading(false);
    }
  };

  const createLandscape = async (landscapeData) => {
    try {
      await axios.post(`${API}/projects/${project.id}/landscapes`, landscapeData);
      await fetchLandscapes();
      setShowCreateLandscape(false);
    } catch (error) {
      console.error('Error creating landscape:', error);
      alert('Failed to create landscape');
    }
  };

  const addSystem = async (landscapeId, systemData) => {
    try {
      await axios.post(`${API}/landscapes/${landscapeId}/systems`, systemData);
      await fetchLandscapes();
      setShowAddSystem(false);
      setSelectedLandscape(null);
    } catch (error) {
      console.error('Error adding system:', error);
      alert('Failed to add system');
    }
  };

  const testConnection = async (systemId) => {
    setTestingConnection(systemId);
    try {
      const response = await axios.post(`${API}/systems/${systemId}/test-connection`);
      
      if (response.data.success) {
        const mode = response.data.mode || 'UNKNOWN';
        const isReal = mode === 'REAL_RFC';
        
        let message = `✅ Connection ${isReal ? 'SUCCESSFUL (Real SAP System)' : 'Test Passed'}!\n\n`;
        message += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        message += `Mode: ${mode}\n`;
        message += `Response Time: ${response.data.response_time_ms}ms\n`;
        message += `SAP Version: ${response.data.sap_version}\n`;
        
        if (response.data.kernel_release) {
          message += `Kernel Release: ${response.data.kernel_release}\n`;
        }
        
        if (response.data.system_info) {
          message += `\n📊 System Information:\n`;
          message += `  SID: ${response.data.system_info.system_id}\n`;
          message += `  Client: ${response.data.system_info.client}\n`;
          message += `  Host: ${response.data.system_info.host}\n`;
          if (response.data.system_info.database_system) {
            message += `  Database: ${response.data.system_info.database_system}\n`;
          }
        }
        
        if (response.data.available_services) {
          message += `\n🔌 Services: ${response.data.available_services.join(', ')}`;
        }
        
        if (response.data.warning) {
          message += `\n\n⚠️ ${response.data.warning}`;
        }
        
        alert(message);
      } else {
        // Connection failed - show detailed error and fixes
        const mode = response.data.mode || 'UNKNOWN';
        let message = `❌ CONNECTION FAILED\n\n`;
        message += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        message += `Mode: ${mode}\n`;
        message += `Error: ${response.data.error}\n\n`;
        
        if (response.data.missing_fields) {
          message += `⚠️ Missing Required Fields:\n`;
          Object.entries(response.data.missing_fields).forEach(([field, missing]) => {
            if (missing) message += `  - ${field}\n`;
          });
          message += `\n`;
        }
        
        if (response.data.connection_params) {
          message += `🔍 Connection Parameters:\n`;
          message += `  Host: ${response.data.connection_params.host}\n`;
          message += `  Instance: ${response.data.connection_params.instance}\n`;
          message += `  Client: ${response.data.connection_params.client}\n`;
          message += `  User: ${response.data.connection_params.user}\n`;
          message += `  Language: ${response.data.connection_params.language}\n`;
          message += `  RFC Port: ${response.data.connection_params.rfc_port}\n\n`;
        }
        
        if (response.data.diagnostics) {
          message += `🔧 Diagnostics:\n`;
          Object.entries(response.data.diagnostics).forEach(([test, result]) => {
            if (result) {
              const status = result.status === 'success' ? '✅' : 
                           result.status === 'failed' ? '❌' : '⚠️';
              message += `  ${status} ${test}: ${result.message || result.status}\n`;
            }
          });
          message += `\n`;
        }
        
        if (response.data.fixes && response.data.fixes.length > 0) {
          message += `💡 RECOMMENDED FIXES:\n\n`;
          response.data.fixes.forEach((fix) => {
            message += `Step ${fix.step}: ${fix.action}\n`;
            message += `  ${fix.details}\n`;
            if (fix.sap_transaction) {
              message += `  SAP Transaction: ${fix.sap_transaction}\n`;
            }
            if (fix.commands && fix.commands.length > 0) {
              message += `  Commands:\n`;
              fix.commands.forEach(cmd => {
                if (cmd.trim()) message += `    ${cmd}\n`;
              });
            }
            message += `\n`;
          });
        }
        
        if (response.data.help_url) {
          message += `📚 Documentation: ${response.data.help_url}`;
        }
        
        alert(message);
      }
    } catch (error) {
      console.error('Connection test error:', error);
      let message = `❌ CONNECTION TEST ERROR\n\n`;
      message += `${error.response?.data?.detail || error.message}\n\n`;
      message += `Please check:\n`;
      message += `1. Backend service is running\n`;
      message += `2. System was saved correctly\n`;
      message += `3. Network connectivity to SAP system`;
      alert(message);
    } finally {
      setTestingConnection(null);
    }
  };

  const toggleSystemExpand = (systemId) => {
    setExpandedSystems({
      ...expandedSystems,
      [systemId]: !expandedSystems[systemId]
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Network className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">SAP Landscape Management</h2>
              <p className="text-blue-100 mt-1">Configure and manage your complete SAP system landscape</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateLandscape(true)}
            className="px-6 py-3 bg-white text-blue-700 rounded-xl hover:bg-blue-50 transition-colors font-medium flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Landscape</span>
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Total Landscapes</p>
          <p className="text-3xl font-bold text-gray-900">{landscapes.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Total Systems</p>
          <p className="text-3xl font-bold text-blue-600">
            {landscapes.reduce((sum, l) => sum + (l.systems_detail?.length || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Connected</p>
          <p className="text-3xl font-bold text-green-600">0</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-sm text-gray-600 mb-1">Configured</p>
          <p className="text-3xl font-bold text-yellow-600">
            {landscapes.reduce((sum, l) => sum + (l.systems_detail?.length || 0), 0)}
          </p>
        </div>
      </div>

      {/* Landscapes */}
      {landscapes.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <Server className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No SAP Landscape Configured</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">Configure your SAP landscape to enable agent-based automation and system integration</p>
          <button
            onClick={() => setShowCreateLandscape(true)}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all font-medium inline-flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Landscape</span>
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {landscapes.map((landscape) => (
            <div key={landscape.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Landscape Header */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <Network className="w-6 h-6 text-blue-600" />
                      <h3 className="text-2xl font-bold text-gray-900">{landscape.name}</h3>
                    </div>
                    <p className="text-gray-600 mb-3">{landscape.description}</p>
                    <div className="flex items-center space-x-3">
                      <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold capitalize">
                        {landscape.landscape_type} Landscape
                      </span>
                      {landscape.network_config?.vpn_required && (
                        <span className="flex items-center space-x-1 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg text-xs font-semibold">
                          <Shield className="w-3 h-3" />
                          <span>VPN Required</span>
                        </span>
                      )}
                      <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold">
                        {landscape.systems_detail?.length || 0} Systems
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedLandscape(landscape.id);
                      setShowAddSystem(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add System</span>
                  </button>
                </div>
              </div>

              {/* Systems Grid */}
              <div className="p-6">
                {landscape.systems_detail && landscape.systems_detail.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {landscape.systems_detail.map((system) => {
                      const isExpanded = expandedSystems[system.id];
                      const statusColor = system.status === 'connected' ? 'green' : 
                                         system.status === 'configured' ? 'yellow' : 'gray';
                      
                      return (
                        <div key={system.id} className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all bg-white">
                          {/* System Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-md">
                                <Database className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-gray-900">{system.system_id}</p>
                                <p className="text-xs text-gray-600 font-medium">{system.system_type}</p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-${statusColor}-100 text-${statusColor}-700`}>
                              {system.status}
                            </span>
                          </div>

                          {/* Quick Info */}
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 flex items-center">
                                <Cpu className="w-4 h-4 mr-2" />
                                Version:
                              </span>
                              <span className="font-bold text-gray-900">{system.version}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 flex items-center">
                                <HardDrive className="w-4 h-4 mr-2" />
                                Client:
                              </span>
                              <span className="font-bold text-gray-900">{system.client}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 flex items-center">
                                <Wifi className="w-4 h-4 mr-2" />
                                Instance:
                              </span>
                              <span className="font-bold text-gray-900">{system.instance_number}</span>
                            </div>
                          </div>

                          {/* Hostname */}
                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">Hostname</p>
                            <p className="text-sm font-mono text-gray-900 truncate">{system.hostname}</p>
                          </div>

                          {/* Modules */}
                          {system.metadata?.modules && system.metadata.modules.length > 0 && (
                            <div className="mb-4">
                              <p className="text-xs text-gray-600 mb-2 font-medium">Active Modules</p>
                              <div className="flex flex-wrap gap-1">
                                {system.metadata.modules.slice(0, 6).map((module) => (
                                  <span key={module} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold">
                                    {module}
                                  </span>
                                ))}
                                {system.metadata.modules.length > 6 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                    +{system.metadata.modules.length - 6}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Capabilities */}
                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-2 font-medium">Permissions</p>
                            <div className="flex flex-wrap gap-2">
                              {system.capabilities?.read_only && (
                                <span className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  <Eye className="w-3 h-3" />
                                  <span>Read Only</span>
                                </span>
                              )}
                              {system.capabilities?.can_execute_code && (
                                <span className="flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                  <Settings className="w-3 h-3" />
                                  <span>Execute Code</span>
                                </span>
                              )}
                              {system.capabilities?.can_create_transports && (
                                <span className="flex items-center space-x-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                                  <Lock className="w-3 h-3" />
                                  <span>Create Transports</span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl space-y-3">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-blue-600 font-medium mb-1">Connection Type</p>
                                  <p className="text-gray-900 font-bold">{system.connection_type}</p>
                                </div>
                                <div>
                                  <p className="text-blue-600 font-medium mb-1">Auth Type</p>
                                  <p className="text-gray-900 font-bold capitalize">{system.credentials?.type}</p>
                                </div>
                                <div>
                                  <p className="text-blue-600 font-medium mb-1">Database</p>
                                  <p className="text-gray-900 font-bold">{system.metadata?.database || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-blue-600 font-medium mb-1">OS</p>
                                  <p className="text-gray-900 font-bold">{system.metadata?.os || 'N/A'}</p>
                                </div>
                              </div>
                              
                              {system.metadata?.add_ons && system.metadata.add_ons.length > 0 && (
                                <div>
                                  <p className="text-blue-600 font-medium mb-2 text-sm">Add-ons</p>
                                  <div className="flex flex-wrap gap-2">
                                    {system.metadata.add_ons.map((addon, idx) => (
                                      <span key={idx} className="px-2 py-1 bg-white border border-blue-200 text-blue-700 rounded text-xs font-medium">
                                        {addon}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex space-x-2">
                            <button
                              onClick={() => testConnection(system.id)}
                              disabled={testingConnection === system.id}
                              className="flex-1 px-3 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold disabled:opacity-50 flex items-center justify-center space-x-2"
                            >
                              {testingConnection === system.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Testing...</span>
                                </>
                              ) : (
                                <>
                                  <Globe className="w-4 h-4" />
                                  <span>Test Connection</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => toggleSystemExpand(system.id)}
                              className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium flex items-center space-x-1"
                            >
                              <Eye className="w-4 h-4" />
                              <span>{isExpanded ? 'Less' : 'More'}</span>
                            </button>
                          </div>

                          {/* Last Connected */}
                          {system.last_connected && (
                            <div className="mt-3 text-xs text-gray-500 text-center">
                              Last connected: {new Date(system.last_connected).toLocaleString()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Server className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600 mb-4">No systems configured in this landscape</p>
                    <button
                      onClick={() => {
                        setSelectedLandscape(landscape.id);
                        setShowAddSystem(true);
                      }}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium inline-flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add First System</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">About SAP Landscape Configuration</h4>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Configure your complete SAP system landscape (DEV, QAS, PRD)</li>
              <li>• Enable AI agents to connect and interact with SAP systems via MCP servers</li>
              <li>• Test connectivity and validate system access</li>
              <li>• Set granular permissions (read-only, execute code, create transports)</li>
              <li>• All connections are secure and use encrypted credentials</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateLandscape && (
        <CreateLandscapeModal 
          onClose={() => setShowCreateLandscape(false)} 
          onCreate={createLandscape} 
        />
      )}
      {showAddSystem && selectedLandscape && (
        <AddSystemModal 
          landscapeId={selectedLandscape} 
          onClose={() => { 
            setShowAddSystem(false); 
            setSelectedLandscape(null); 
          }} 
          onAdd={addSystem}
          onComplete={fetchLandscapes}
        />
      )}
    </div>
  );
};

const CreateLandscapeModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    landscape_type: '3-tier',
    vpn_required: true
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    await onCreate(formData);
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-2xl">
        <h3 className="text-3xl font-bold text-gray-900 mb-6">Create SAP Landscape</h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Landscape Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Production Landscape"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Describe the landscape purpose and configuration"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Landscape Type *</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: '3-tier', label: '3-Tier', desc: 'DEV, QAS, PRD' },
                { value: '2-tier', label: '2-Tier', desc: 'QAS, PRD' },
                { value: 'sandbox', label: 'Sandbox', desc: 'Sandbox Only' },
                { value: 'custom', label: 'Custom', desc: 'Custom Setup' }
              ].map((type) => (
                <label
                  key={type.value}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.landscape_type === type.value
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="landscape_type"
                    value={type.value}
                    checked={formData.landscape_type === type.value}
                    onChange={(e) => setFormData({ ...formData, landscape_type: e.target.value })}
                    className="hidden"
                  />
                  <p className="font-bold text-gray-900">{type.label}</p>
                  <p className="text-xs text-gray-600 mt-1">{type.desc}</p>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center space-x-3 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl cursor-pointer hover:bg-yellow-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.vpn_required}
              onChange={(e) => setFormData({ ...formData, vpn_required: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <div>
              <p className="font-semibold text-gray-900 flex items-center">
                <Shield className="w-4 h-4 mr-2 text-yellow-600" />
                VPN Required
              </p>
              <p className="text-sm text-gray-600">Systems require VPN connection for secure access</p>
            </div>
          </label>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {creating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Create Landscape</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddSystemModal = ({ landscapeId, onClose, onAdd, onComplete }) => {
  const [formData, setFormData] = useState({
    system_id: '',
    system_type: 'ECC',
    version: 'ECC 6.0 EHP8',
    hostname: '',
    instance_number: '00',
    client: '100',
    connection_type: 'RFC',
    auth_type: 'user_password',
    credential_id: '',
    username: '',
    password: '',
    language: 'EN',
    read_only: true,
    can_execute_code: false,
    can_create_transports: false,
    modules: [],
    add_ons: [],
    database: 'HANA',
    os: 'Linux'
  });
  const [adding, setAdding] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAdding(true);
    await onAdd(landscapeId, formData);
    await onComplete();
    setAdding(false);
  };

  const sapModules = ['FI', 'CO', 'SD', 'MM', 'PP', 'QM', 'PM', 'HR', 'WM', 'EWM', 'PS', 'IM'];
  const sapAddOns = ['IS-Oil', 'IS-Retail', 'IS-Utilities', 'SAP GTS', 'SAP APO', 'SAP CRM', 'SAP BW'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h3 className="text-3xl font-bold text-gray-900 mb-6">Add SAP System to Landscape</h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System ID *</label>
              <input
                type="text"
                required
                value={formData.system_id}
                onChange={(e) => setFormData({ ...formData, system_id: e.target.value.toUpperCase() })}
                placeholder="PRD / QAS / DEV"
                maxLength="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase font-bold"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System Type *</label>
              <select
                value={formData.system_type}
                onChange={(e) => setFormData({ ...formData, system_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ECC">SAP ECC</option>
                <option value="S/4HANA">SAP S/4HANA</option>
                <option value="BW">SAP BW</option>
                <option value="CRM">SAP CRM</option>
                <option value="SRM">SAP SRM</option>
                <option value="GTS">SAP GTS</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Version *</label>
              <input
                type="text"
                required
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="ECC 6.0 EHP8"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Connection Info */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hostname *</label>
              <input
                type="text"
                required
                value={formData.hostname}
                onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                placeholder="sap-prd.company.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Instance</label>
                <input
                  type="text"
                  value={formData.instance_number}
                  onChange={(e) => setFormData({ ...formData, instance_number: e.target.value })}
                  placeholder="00"
                  maxLength="2"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-bold text-center"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Client</label>
                <input
                  type="text"
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  placeholder="100"
                  maxLength="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-bold text-center"
                />
              </div>
            </div>
          </div>

          {/* SAP Credentials */}
          <div className="p-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="w-5 h-5 text-yellow-600" />
              <h4 className="font-bold text-gray-900">SAP RFC Credentials</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Required for real SAP system connection testing and data extraction</p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SAP Username *</label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="RFC_USER"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SAP Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Language</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="EN">English (EN)</option>
                  <option value="DE">German (DE)</option>
                  <option value="FR">French (FR)</option>
                  <option value="ES">Spanish (ES)</option>
                  <option value="PT">Portuguese (PT)</option>
                  <option value="ZH">Chinese (ZH)</option>
                  <option value="JA">Japanese (JA)</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Credentials are encrypted and stored securely. Only used for RFC connection testing and authorized data extraction.
              </p>
            </div>
          </div>

          {/* Modules Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Active SAP Modules</label>
            <div className="grid grid-cols-6 gap-2">
              {sapModules.map((module) => (
                <label
                  key={module}
                  className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all hover:scale-105 ${
                    formData.modules.includes(module)
                      ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 text-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.modules.includes(module)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, modules: [...formData.modules, module] });
                      } else {
                        setFormData({ ...formData, modules: formData.modules.filter(m => m !== module) });
                      }
                    }}
                    className="hidden"
                  />
                  <span className="text-sm font-bold">{module}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">SAP Add-ons (Optional)</label>
            <div className="grid grid-cols-4 gap-2">
              {sapAddOns.map((addon) => (
                <label
                  key={addon}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center transition-all ${
                    formData.add_ons.includes(addon)
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-purple-300 text-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.add_ons.includes(addon)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, add_ons: [...formData.add_ons, addon] });
                      } else {
                        setFormData({ ...formData, add_ons: formData.add_ons.filter(a => a !== addon) });
                      }
                    }}
                    className="hidden"
                  />
                  <span className="text-xs font-medium">{addon}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Technical Config */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Database</label>
              <select
                value={formData.database}
                onChange={(e) => setFormData({ ...formData, database: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="HANA">SAP HANA</option>
                <option value="Oracle">Oracle</option>
                <option value="DB2">IBM DB2</option>
                <option value="SQL Server">Microsoft SQL Server</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Operating System</label>
              <select
                value={formData.os}
                onChange={(e) => setFormData({ ...formData, os: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Linux">Linux</option>
                <option value="Windows">Windows Server</option>
                <option value="AIX">IBM AIX</option>
                <option value="HP-UX">HP-UX</option>
              </select>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">System Capabilities</label>
            <div className="grid grid-cols-3 gap-4">
              <label className="flex items-center space-x-3 p-4 bg-green-50 border-2 border-green-200 rounded-xl cursor-pointer hover:bg-green-100 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.read_only}
                  onChange={(e) => setFormData({ ...formData, read_only: e.target.checked })}
                  className="w-5 h-5 text-green-600 rounded"
                />
                <div>
                  <p className="font-semibold text-gray-900 flex items-center text-sm">
                    <Eye className="w-4 h-4 mr-2 text-green-600" />
                    Read Only
                  </p>
                  <p className="text-xs text-gray-600">Recommended for safety</p>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-4 bg-orange-50 border-2 border-orange-200 rounded-xl cursor-pointer hover:bg-orange-100 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.can_execute_code}
                  onChange={(e) => setFormData({ ...formData, can_execute_code: e.target.checked })}
                  className="w-5 h-5 text-orange-600 rounded"
                />
                <div>
                  <p className="font-semibold text-gray-900 flex items-center text-sm">
                    <Settings className="w-4 h-4 mr-2 text-orange-600" />
                    Execute Code
                  </p>
                  <p className="text-xs text-gray-600">For remediation</p>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl cursor-pointer hover:bg-purple-100 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.can_create_transports}
                  onChange={(e) => setFormData({ ...formData, can_create_transports: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded"
                />
                <div>
                  <p className="font-semibold text-gray-900 flex items-center text-sm">
                    <Lock className="w-4 h-4 mr-2 text-purple-600" />
                    Create Transports
                  </p>
                  <p className="text-xs text-gray-600">For deployment</p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={adding}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {adding ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Adding System...</span>
                </>
              ) : (
                <>
                  <Database className="w-5 h-5" />
                  <span>Add System</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
